import { getImageBackgroundColor } from '@/Components/posts/utils';
import { getCatalogMediaLinks } from '@/Components/posts/utils/catalog';
import { Analytics } from '@/Telemetry';
import { useIframeMediaHandler } from '@/Telemetry/MediaHandler';
import { useHtmlVideoHandler } from '@/Telemetry/MediaHandler/htmlVideo';
import { useAsyncMemo, useBreakpoints } from '@/Utility/hooks';
import { MediaItemSizeEnum } from '@/graphql/types';
import { type AppStore, useAppStore } from '@/inertia/AppStore';
import { Paper, Skeleton } from '@mui/material';
import { Link as TsrLink } from '@tanstack/react-router';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { usePlayerStore } from './usePlayerStore';

const IFRAME_CREDS = {
  credentialless: 'true',
  crossOrigin: 'use-credentials',
};

const AnalyticsCallbacks = {
  onImageClick() {
    Analytics.push('zngly_media_interaction', {
      eventaction: 'click',
      eventlabel: 'zngly_media_default',
    });
  },
  onImageLoad() {
    setTimeout(() => {
      Analytics.push('zngly_media_interaction', {
        eventaction: 'view',
        eventlabel: 'zngly_media_default',
      });
    }, 2000);
  },
};

type Props = {
  node: AppStore['playerNode'];
};

const PlayerElement = ({ node }: Props) => {
  const isAppPreview = useAppStore((state) => state.isAppPreview);

  const contentMediaName = useMemo(() => node?.znglyContent?.contentMediaName, [node?.znglyContent?.contentMediaName]);
  const { srcLink, featuredImage } = getCatalogMediaLinks(node?.znglyContent, MediaItemSizeEnum.Large);

  const backgroundColor = useAsyncMemo(async () => {
    if (contentMediaName === 'IMAGE' || contentMediaName === 'LINK' || (!!srcLink && !!featuredImage)) {
      return await getImageBackgroundColor(featuredImage);
    }
    return 'unset';
  }, [contentMediaName, srcLink, featuredImage]);

  const style = {
    backgroundColor: backgroundColor,
  } as React.CSSProperties;

  if (!node || !Object.keys(node)?.length) {
    return <NoPlayerFound />;
  }

  if (node?.znglyContent?.contentMediaType?.toLowerCase() === 'embed') {
    return <HtmlIframe node={node} isAppPreview={isAppPreview} />;
  }
  if (contentMediaName === 'PDF') {
    return <PdfIframe node={node} srcLink={srcLink} featuredImage={featuredImage} isAppPreview={isAppPreview} />;
  }
  if (contentMediaName === 'MP4') {
    return <HtmlVideo node={node} srcLink={srcLink} featuredImage={featuredImage} isAppPreview={isAppPreview} />;
  }
  if (contentMediaName === 'IMAGE') {
    return (
      <div className="Player-Responsive-Image" style={style}>
        <HtmlImage
          isAppPreview={isAppPreview}
          featuredImage={(!!srcLink ? srcLink : featuredImage) ?? ''}
          onClick={AnalyticsCallbacks.onImageClick}
          onLoad={AnalyticsCallbacks.onImageLoad}
        />
      </div>
    );
  }
  if (contentMediaName === 'LINK') {
    return (
      <div className="Player-Responsive-Image" style={style}>
        <a target="_blank" href={srcLink ?? ''} rel="noreferrer" onClick={AnalyticsCallbacks.onImageClick}>
          <HtmlImage featuredImage={featuredImage ?? ''} isAppPreview={isAppPreview} />
        </a>
      </div>
    );
  }
  if (!!srcLink && !!featuredImage) {
    return (
      <div className="Player-Responsive-Image" style={style}>
        <a target="_blank" href={srcLink} rel="noreferrer" onClick={AnalyticsCallbacks.onImageClick}>
          <HtmlImage featuredImage={featuredImage} isAppPreview={isAppPreview} />
        </a>
      </div>
    );
  }

  return <NoPlayerFound />;
};

type ElProps = {
  srcLink?: string | null;
  featuredImage?: string | null;
  isAppPreview: boolean;
} & Props;

const PdfIframeMobile = ({ srcLink, featuredImage, isAppPreview }: ElProps) => {
  useEffect(() => {
    usePlayerStore.setState({ disabledPlayerBar: true });

    return () => {
      usePlayerStore.setState({ disabledPlayerBar: false });
    };
  }, []);

  return (
    <a href={srcLink ?? ''} target="_blank" style={{ cursor: 'pointer', position: 'relative' }} rel="noreferrer">
      <HtmlImage featuredImage={featuredImage ?? ''} isAppPreview={isAppPreview} />
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 1,
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          color: 'white',
          fontSize: '1.5rem',
          fontWeight: 'bold',
        }}
      >
        <p>Click to view PDF</p>
      </div>
    </a>
  );
};

const PdfIframe = ({ node, srcLink, featuredImage, isAppPreview }: ElProps) => {
  const events = useIframeMediaHandler({ node });
  const { isMobile } = useBreakpoints();

  const hasPdfReader = useMemo(() => {
    return window.navigator.pdfViewerEnabled;
  }, []);

  if (!hasPdfReader || isMobile) {
    return <PdfIframeMobile node={node} srcLink={srcLink} featuredImage={featuredImage} isAppPreview={isAppPreview} />;
  }

  return (
    <PlayerLoadWrapper>
      <iframe
        title={node?.title ?? ''}
        className="zn-pdf-iframe"
        src={srcLink ?? ''}
        height="600px"
        style={{ height: '100%', width: '100%' }}
        {...events}
        {...(isAppPreview && {
          ...IFRAME_CREDS,
        })}
      />
    </PlayerLoadWrapper>
  );
};

const HtmlVideo = ({ srcLink }: ElProps) => {
  const events = useHtmlVideoHandler();

  return (
    <PlayerLoadWrapper>
      <video width="100%" height="auto" controls {...events}>
        <source src={srcLink ?? ''} />
        Your browser does not support HTML video.
      </video>
    </PlayerLoadWrapper>
  );
};

const HtmlImage = ({
  featuredImage,
  isAppPreview,
  ...props
}: { featuredImage: string; isAppPreview: boolean } & React.DetailedHTMLProps<
  React.ImgHTMLAttributes<HTMLImageElement>,
  HTMLImageElement
>) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setLoaded(true);
    }, 250);
  }, []);

  const allProps = {
    ...props,
    className: `${props?.className} ${loaded ? 'z-show' : 'z-hide'}`,
    onLoad: (e) => {
      setLoaded(true);
      props?.onLoad?.(e);
    },
  } as React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement>;

  if (!loaded) {
    return <Skeleton variant="rectangular" width="100%" height="100%" />;
  }
  return <img src={featuredImage} width="100%" height="100%" {...allProps} />;
};

const HtmlIframe = ({ node, isAppPreview }: Props & { isAppPreview: boolean }) => {
  const iframe = useMemo(() => {
    const dom = new DOMParser().parseFromString(node?.znglyContent?.contentEmbedHtml ?? '', 'text/html');
    return dom.querySelector('iframe') as HTMLIFrameElement;
  }, [node?.znglyContent?.contentEmbedHtml]);

  const events = useIframeMediaHandler({ node });

  if (!iframe) {
    return <NoPlayerFound />;
  }

  return (
    <PlayerLoadWrapper>
      <iframe
        title={iframe?.title ?? 'zngly-iframe-embed'}
        width="100%"
        height="100%"
        src={iframe.src}
        {...(isAppPreview && {
          ...IFRAME_CREDS,
        })}
        {...events}
        {...(iframe.getAttribute('allowfullscreen') === 'true' && { allowFullScreen: true })}
        {...(iframe.getAttribute('frameborder') && { frameBorder: iframe.getAttribute('frameborder') ?? '' })}
        {...(iframe.getAttribute('allow') && { allow: iframe.getAttribute('allow') ?? '' })}
        {...(iframe?.loading === 'lazy' && { loading: 'lazy' })}
      />
    </PlayerLoadWrapper>
  );
};

const NoPlayerFound = () => {
  const isAppPreview = useAppStore((state) => state.isAppPreview);

  return (
    <PlayerLoadWrapper>
      <Paper
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          border: '1px solid #ccc',
        }}
      >
        {isAppPreview ? (
          <p>Preview Mode</p>
        ) : (
          <p>
            No Data Found <TsrLink to="/">Go Home</TsrLink>
          </p>
        )}
      </Paper>
    </PlayerLoadWrapper>
  );
};

type PlayerLoadWrapperProps = { children: React.ReactNode };

const PlayerLoadWrapper = (props: PlayerLoadWrapperProps) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    setTimeout(() => {
      setLoaded(true);
    }, 250);

    return () => {
      setLoaded(false);
    };
  }, []);

  return (
    <div className={`player-load-wrapper ${loaded ? 'z-show' : 'z-hide'}`} style={{ width: '100%', height: '100%' }}>
      {props?.children}
      {!loaded && (
        <Skeleton variant="rectangular" animation="wave" width="100%" height="100%" sx={{ backgroundColor: 'gray' }} />
      )}
    </div>
  );
};

export const PlayerElementFallback = () => {
  return (
    <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <Skeleton variant="rectangular" animation="wave" width="100%" height="100%" sx={{ backgroundColor: 'gray' }} />
    </div>
  );
};

export { PlayerElement };
